import React from 'react';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { fetchAndSaveThumbnail } from "../../utils/verkadaHelpers";

const KasseModule = ({
  // Translation
  t,
  lang,
  isMobile,
  API,
  session,
  
  // Kasse state
  kasseItems,
  setKasseItems,
  kasseTotalAmount,
  setKasseTotalAmount,
  kasseCustomerName,
  setKasseCustomerName,
  kasseNewItemName,
  setKasseNewItemName,
  kasseNewItemPrice,
  setKasseNewItemPrice,
  kasseNewItemQuantity,
  setKasseNewItemQuantity,
  kasseEvents,
  setKasseEvents,
  kasseSearch,
  setKasseSearch,
  kasseSortBy,
  setKasseSortBy,
  kasseSortOrder,
  setKasseSortOrder,
  selectedKasseReceipt,
  setSelectedKasseReceipt,
  savedKassenCameraID,
  setSelectedThumbnail,
  
  // Functions
  loadKasseEvents
}) => {

  // Add item to cart
  const addKasseItem = () => {
    if (!kasseNewItemName.trim() || !kasseNewItemPrice.trim()) {
      alert("Bitte Name und Preis eingeben");
      return;
    }
    
    const price = parseFloat(kasseNewItemPrice);
    if (isNaN(price) || price <= 0) {
      alert("Bitte gültigen Preis eingeben");
      return;
    }

    const newItem = {
      id: Date.now(),
      name: kasseNewItemName.trim(),
      price: price,
      quantity: kasseNewItemQuantity,
      total: price * kasseNewItemQuantity
    };

    setKasseItems(items => [...items, newItem]);
    setKasseTotalAmount(total => total + newItem.total);
    
    // Clear inputs
    setKasseNewItemName("");
    setKasseNewItemPrice("");
    setKasseNewItemQuantity(1);
  };

  // Add quick item to cart
  const addQuickItem = (name, price) => {
    const newItem = {
      id: Date.now(),
      name: name,
      price: price,
      quantity: 1,
      total: price
    };

    setKasseItems(items => [...items, newItem]);
    setKasseTotalAmount(total => total + newItem.total);
  };

  // Remove item from cart
  const removeKasseItem = (itemId) => {
    const item = kasseItems.find(item => item.id === itemId);
    if (item) {
      setKasseItems(items => items.filter(i => i.id !== itemId));
      setKasseTotalAmount(total => total - item.total);
    }
  };

  // Clear cart
  const clearKasse = () => {
    setKasseItems([]);
    setKasseTotalAmount(0);
    setKasseCustomerName("");
  };

  // Complete transaction
  const completeKasseTransaction = async () => {
    if (kasseItems.length === 0) {
      alert("Keine Artikel im Warenkorb");
      return;
    }
    
    const timestamp = new Date().toISOString();
    const berlinTime = new Date().toLocaleString('de-DE', {
      timeZone: 'Europe/Berlin',
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    
    
    const transactionData = {
      customer_name: kasseCustomerName || "",
      items: kasseItems,
      total_amount: kasseTotalAmount,
      timestamp: timestamp
    };

    const axios = (await import('axios')).default;

    try {
      const response = await axios.post(`${API}/user/kasse/transaction`, {
        transaction: transactionData
      }, { headers: session.authHeaders() });

      const data = response.data;
      
      if (data?.success) {
        alert(`✅ Zahlung erfolgreich abgeschlossen!\n\nKunde: ${kasseCustomerName || "Kunde"}\nGesamt: €${kasseTotalAmount.toFixed(2)}\nZeit: ${berlinTime}\n\n🎯 Verkada Video Tagging Event erfolgreich gesendet`);
        loadKasseEvents();
        
        // Wait 3 seconds then fetch thumbnail
        fetchAndSaveThumbnail({
          module: 'kasse',
          cameraId: savedKassenCameraID,
          timestampMs: data.timestamp_ms,
          savePayload: { epoch_timestamp: data.timestamp_ms },
          userToken: session.userToken,
          onSuccess: loadKasseEvents
        });
      } else if (data?.transaction_completed) {
        alert(`⚠️ Zahlung abgeschlossen (Verkada Integration teilweise)!\n\nKunde: ${kasseCustomerName || "Kunde"}\nGesamt: €${kasseTotalAmount.toFixed(2)}\nZeit: ${berlinTime}\n\n📋 Transaktion: Erfolgreich\n🔗 Verkada: ${data.message}`);
        loadKasseEvents();
        
        fetchAndSaveThumbnail({
          module: 'kasse',
          cameraId: savedKassenCameraID,
          timestampMs: data.timestamp_ms,
          savePayload: { epoch_timestamp: data.timestamp_ms },
          userToken: session.userToken,
          onSuccess: loadKasseEvents
        });
      } else {
        alert(`❌ Zahlung fehlgeschlagen!\n\nFehler: ${data?.error || "Unbekannter Fehler"}`);
        return;
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || "Netzwerk- oder Server-Fehler";
      alert(`❌ Zahlung fehlgeschlagen!\n\nFehler: ${errorMsg}\n\nBitte versuchen Sie es erneut oder wenden Sie sich an den Support.`);
      return;
    }
    
    clearKasse();
  };

  // Process return
  const processKasseReturn = async () => {
    if (kasseItems.length === 0) {
      alert("Keine Artikel für Rücknahme vorhanden");
      return;
    }
    
    const timestamp = new Date().toISOString();
    const berlinTime = new Date().toLocaleString('de-DE', {
      timeZone: 'Europe/Berlin',
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    
    
    const returnAmount = kasseTotalAmount;
    const customer = kasseCustomerName || "Kunde";
    
    const transactionData = {
      customer_name: customer,
      items: kasseItems,
      total_amount: returnAmount,
      timestamp: timestamp
    };

    const axios = (await import('axios')).default;

    try {
      const response = await axios.post(`${API}/user/kasse/transaction`, {
        transaction: transactionData,
        flagged: true
      }, { headers: session.authHeaders() });

      const data = response.data;
      
      if (data?.success) {
        alert(`✅ Rücknahme erfolgreich verarbeitet!\n\nKunde: ${customer}\nRückerstattung: €${returnAmount.toFixed(2)}\nZeit: ${berlinTime}\n\n🎯 Verkada Video Tagging Event (flagged) erfolgreich gesendet`);
        loadKasseEvents();
        
        fetchAndSaveThumbnail({
          module: 'kasse',
          cameraId: savedKassenCameraID,
          timestampMs: data.timestamp_ms,
          savePayload: { epoch_timestamp: data.timestamp_ms },
          userToken: session.userToken,
          onSuccess: loadKasseEvents
        });
      } else if (data?.transaction_completed) {
        alert(`⚠️ Rücknahme verarbeitet (Verkada Integration teilweise)!\n\nKunde: ${customer}\nRückerstattung: €${returnAmount.toFixed(2)}\nZeit: ${berlinTime}\n\n📋 Rücknahme: Erfolgreich\n🔗 Verkada: ${data.message}`);
        loadKasseEvents();
        
        fetchAndSaveThumbnail({
          module: 'kasse',
          cameraId: savedKassenCameraID,
          timestampMs: data.timestamp_ms,
          savePayload: { epoch_timestamp: data.timestamp_ms },
          userToken: session.userToken,
          onSuccess: loadKasseEvents
        });
      } else {
        alert(`❌ Rücknahme fehlgeschlagen!\n\nFehler: ${data?.error || "Unbekannter Fehler"}`);
        return;
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || "Netzwerk- oder Server-Fehler";
      alert(`❌ Rücknahme fehlgeschlagen!\n\nFehler: ${errorMsg}\n\nBitte versuchen Sie es erneut oder wenden Sie sich an den Support.`);
      return;
    }
    
    clearKasse();
  };

  // Filter and sort kasse events
  const getFilteredAndSortedKasseEvents = () => {
    let filtered = [...kasseEvents];
    
    // Apply search filter
    if (kasseSearch.trim()) {
      const searchLower = kasseSearch.toLowerCase();
      filtered = filtered.filter(event => 
        (event.customer_name && event.customer_name.toLowerCase().includes(searchLower)) ||
        event.items.some(item => item.name.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      if (kasseSortBy === 'timestamp') {
        comparison = new Date(a.timestamp) - new Date(b.timestamp);
      } else if (kasseSortBy === 'customer') {
        comparison = (a.customer_name || '').localeCompare(b.customer_name || '');
      } else if (kasseSortBy === 'total') {
        comparison = a.total_amount - b.total_amount;
      }
      
      if (kasseSortOrder === 'asc') {
        return comparison;
      }
      return -comparison;
    });
    
    return filtered;
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
      
      {/* Kasse Header */}
      <Card className="panel lightCard" style={{marginTop: 8}}>
        <CardContent>
          <div className="panel-title darktxt">💰 {t('kasse_title')}</div>
          <div className="small">{t('kasse_subtitle')}</div>
        </CardContent>
      </Card>

      <div style={{
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', 
        gap: isMobile ? '12px' : '16px'
      }}>
        
        {/* Artikel hinzufügen */}
        <Card className="panel lightCard">
          <CardContent>
            <div className="panel-title darktxt">➕ {t('kasse_add_item')}</div>
            
            <div className={isMobile ? "field" : "field twoCol"}>
              <div>
                <Label>{t('kasse_item_name')}</Label>
                <Input 
                  value={kasseNewItemName} 
                  onChange={(e) => setKasseNewItemName(e.target.value)} 
                  placeholder={t('item_placeholder')} 
                />
              </div>
              <div>
                <Label>{t('kasse_item_price')}</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  value={kasseNewItemPrice} 
                  onChange={(e) => setKasseNewItemPrice(e.target.value)} 
                  placeholder="0.00" 
                />
              </div>
            </div>
            
            <div className="field">
              <Label>{t('kasse_item_quantity')}</Label>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setKasseNewItemQuantity(Math.max(1, kasseNewItemQuantity - 1))}
                >
                  -
                </Button>
                <span style={{minWidth: '40px', textAlign: 'center', fontWeight: 'bold'}}>{kasseNewItemQuantity}</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setKasseNewItemQuantity(kasseNewItemQuantity + 1)}
                >
                  +
                </Button>
              </div>
            </div>

            <div className="actions">
              <Button onClick={addKasseItem} disabled={!kasseNewItemName.trim() || !kasseNewItemPrice.trim()}>
                🛒 {t('kasse_add_to_cart')}
              </Button>
            </div>

            {/* Quick Add Buttons */}
            <div style={{marginTop: 16}}>
              <div style={{fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#374151'}}>
                ⚡ {t('kasse_quick_add')}
              </div>
              <div style={{
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', 
                gap: '8px'
              }}>
                <Button 
                  variant="outline" 
                  onClick={() => addQuickItem('Caffee', 3.00)}
                  style={{
                    padding: isMobile ? '8px 4px' : '12px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: isMobile ? '70px' : '80px',
                    fontSize: isMobile ? '10px' : '12px'
                  }}
                >
                  <div style={{fontSize: '24px'}}>☕</div>
                  <div style={{fontWeight: '500'}}>Caffee</div>
                  <div style={{color: '#059669'}}>€3,00</div>
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={() => addQuickItem('Sandwich', 5.00)}
                  style={{
                    padding: isMobile ? '8px 4px' : '12px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: isMobile ? '70px' : '80px',
                    fontSize: isMobile ? '10px' : '12px'
                  }}
                >
                  <div style={{fontSize: '24px'}}>🥪</div>
                  <div style={{fontWeight: '500'}}>Sandwich</div>
                  <div style={{color: '#059669'}}>€5,00</div>
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={() => addQuickItem('Drink', 2.00)}
                  style={{
                    padding: isMobile ? '8px 4px' : '12px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: isMobile ? '70px' : '80px',
                    fontSize: isMobile ? '10px' : '12px'
                  }}
                >
                  <div style={{fontSize: '24px'}}>🥤</div>
                  <div style={{fontWeight: '500'}}>Drink</div>
                  <div style={{color: '#059669'}}>€2,00</div>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Kassenbereich */}
        <Card className="panel lightCard">
          <CardContent>
            <div className="panel-title darktxt">🧾 Aktuelle Rechnung</div>
            
            <div className="field">
              <Label>{t('kasse_customer_name')}</Label>
              <Input 
                value={kasseCustomerName} 
                onChange={(e) => setKasseCustomerName(e.target.value)} 
                placeholder="Kundenname" 
              />
            </div>

            {/* Gesamtsumme */}
            <div style={{
              backgroundColor: '#f0f9ff',
              padding: '16px',
              borderRadius: '8px',
              border: '2px solid #3b82f6',
              textAlign: 'center',
              marginBottom: '12px'
            }}>
              <div className="small" style={{color: '#1e40af', marginBottom: '4px'}}>{t('kasse_total_amount')}</div>
              <div style={{fontSize: '24px', fontWeight: 'bold', color: '#1e40af'}}>
                €{kasseTotalAmount.toFixed(2)}
              </div>
            </div>

            <div className="actions">
              <Button 
                onClick={completeKasseTransaction} 
                disabled={kasseItems.length === 0}
                variant="default"
                style={{width: '100%', marginBottom: '8px'}}
              >
                💳 {t('kasse_complete_payment')}
              </Button>
              <Button 
                onClick={processKasseReturn} 
                disabled={kasseItems.length === 0}
                variant="outline"
                style={{width: '100%', marginBottom: '8px', color: '#dc2626', borderColor: '#fca5a5'}}
              >
                ↩️ {t('kasse_process_return')}
              </Button>
              <Button 
                onClick={clearKasse} 
                disabled={kasseItems.length === 0}
                variant="secondary"
                style={{width: '100%'}}
              >
                🗑️ {t('kasse_clear_cart')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Artikelliste */}
      <Card className="panel lightCard">
        <CardContent>
          <div className="panel-title darktxt">🛍️ {t('kasse_cart')} ({kasseItems.length} Artikel)</div>
          
          {kasseItems.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '32px',
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '2px dashed #d1d5db'
            }}>
              <div style={{fontSize: '48px', marginBottom: '8px'}}>🛒</div>
              <div>{t('kasse_empty_cart')}</div>
              <div className="small">{t('kasse_empty_cart_desc')}</div>
            </div>
          ) : (
            <div style={{overflowX: 'auto'}}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead style={{fontSize: isMobile ? '12px' : '14px'}}>
                      {t('kasse_item_article')}
                    </TableHead>
                    <TableHead style={{
                      textAlign: 'center',
                      fontSize: isMobile ? '12px' : '14px',
                      display: isMobile ? 'none' : 'table-cell'
                    }}>
                      {t('kasse_item_quantity_short')}
                    </TableHead>
                    <TableHead style={{
                      textAlign: 'right',
                      fontSize: isMobile ? '12px' : '14px',
                      display: isMobile ? 'none' : 'table-cell'
                    }}>
                      {t('kasse_item_unit_price')}
                    </TableHead>
                    <TableHead style={{
                      textAlign: 'right',
                      fontSize: isMobile ? '12px' : '14px'
                    }}>
                      {isMobile ? 'Gesamt' : t('kasse_item_total_price')}
                    </TableHead>
                    <TableHead style={{
                      textAlign: 'center',
                      fontSize: isMobile ? '12px' : '14px'
                    }}>
                      {isMobile ? '' : t('kasse_action')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {kasseItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell style={{
                        fontWeight: '500',
                        fontSize: isMobile ? '12px' : '14px'
                      }}>
                        {item.name}
                        {isMobile && (
                          <div style={{fontSize: '10px', color: '#6b7280', marginTop: '2px'}}>
                            {item.quantity}x × €{item.price.toFixed(2)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell style={{
                        textAlign: 'center',
                        fontSize: isMobile ? '12px' : '14px',
                        display: isMobile ? 'none' : 'table-cell'
                      }}>
                        {item.quantity}x
                      </TableCell>
                      <TableCell style={{
                        textAlign: 'right',
                        fontSize: isMobile ? '12px' : '14px',
                        display: isMobile ? 'none' : 'table-cell'
                      }}>
                        €{item.price.toFixed(2)}
                      </TableCell>
                      <TableCell style={{
                        textAlign: 'right', 
                        fontWeight: 'bold',
                        fontSize: isMobile ? '12px' : '14px'
                      }}>
                        €{item.total.toFixed(2)}
                      </TableCell>
                      <TableCell style={{textAlign: 'center'}}>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => removeKasseItem(item.id)}
                          style={{
                            color: '#dc2626', 
                            borderColor: '#fca5a5',
                            padding: isMobile ? '4px 8px' : '6px 12px',
                            fontSize: isMobile ? '10px' : '12px'
                          }}
                        >
                          🗑️
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction History Table */}
      <Card className="panel lightCard">
        <CardContent>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
            <div>
              <div className="panel-title darktxt">📊 {t('kasse_history_title')}</div>
              <div className="small">{t('kasse_history_subtitle')}</div>
            </div>
          </div>

          {/* Search Input */}
          <div style={{marginBottom: '16px'}}>
            <Input
              placeholder={t('kasse_search_placeholder')}
              value={kasseSearch}
              onChange={(e) => setKasseSearch(e.target.value)}
              style={{maxWidth: '400px'}}
            />
          </div>

          {/* Table */}
          <div style={{overflowX: 'auto'}}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '13px'
            }}>
              <thead>
                <tr style={{
                  borderBottom: '2px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}>
                  <th
                    onClick={() => {
                      if (kasseSortBy === 'timestamp') {
                        setKasseSortOrder(kasseSortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setKasseSortBy('timestamp');
                        setKasseSortOrder('desc');
                      }
                    }}
                    style={{
                      padding: '12px 8px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontWeight: '600',
                      color: '#374151'
                    }}
                  >
                    {t('kasse_table_datetime')} {kasseSortBy === 'timestamp' && (kasseSortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{
                    padding: '12px 8px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    {t('kasse_table_items')}
                  </th>
                  <th
                    onClick={() => {
                      if (kasseSortBy === 'customer') {
                        setKasseSortOrder(kasseSortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setKasseSortBy('customer');
                        setKasseSortOrder('asc');
                      }
                    }}
                    style={{
                      padding: '12px 8px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontWeight: '600',
                      color: '#374151'
                    }}
                  >
                    {t('kasse_table_customer')} {kasseSortBy === 'customer' && (kasseSortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#374151',
                    width: '100px'
                  }}>
                    {t('kasse_table_thumbnail')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const filteredEvents = getFilteredAndSortedKasseEvents();
                  
                  if (kasseEvents.length === 0) {
                    return (
                      <tr>
                        <td colSpan="4" style={{
                          padding: '24px',
                          textAlign: 'center',
                          color: '#6b7280'
                        }}>
                          {t('kasse_table_empty')}
                        </td>
                      </tr>
                    );
                  }
                  
                  if (filteredEvents.length === 0) {
                    return (
                      <tr>
                        <td colSpan="4" style={{
                          padding: '24px',
                          textAlign: 'center',
                          color: '#6b7280'
                        }}>
                          {t('kasse_search_no_results')}
                        </td>
                      </tr>
                    );
                  }
                  
                  return filteredEvents.map((event, index) => {
                    // Format timestamp to dd.mm.yy hh:mm:ss
                    let formattedDate = '';
                    let formattedTime = '';
                    if (event.timestamp) {
                      const date = new Date(event.timestamp);
                      formattedTime = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                      formattedDate = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
                    }
                    
                    // Format items
                    const itemsText = event.items.map(item => 
                      `${item.quantity}x ${item.name}`
                    ).join(', ');
                    
                    return (
                      <tr key={index} 
                      onClick={() => setSelectedKasseReceipt(event)}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                        cursor: 'pointer'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                      >
                        <td style={{padding: '12px 8px'}}>
                          <div style={{lineHeight: '1.4'}}>
                            <div style={{fontWeight: '500'}}>{formattedTime}</div>
                            <div style={{fontSize: '12px', color: '#6b7280'}}>{formattedDate}</div>
                          </div>
                        </td>
                        <td style={{padding: '12px 8px'}}>
                          <div style={{
                            maxWidth: '300px',
                            fontSize: '12px',
                            lineHeight: '1.4'
                          }}>
                            {itemsText}
                            <div style={{
                              fontSize: '11px',
                              color: event.flagged ? '#dc2626' : '#059669',
                              marginTop: '4px',
                              fontWeight: '500'
                            }}>
                              {event.flagged ? '↩️ Rücknahme' : '💳 Verkauf'}: €{event.total_amount.toFixed(2)}
                            </div>
                          </div>
                        </td>
                        <td style={{padding: '12px 8px'}}>
                          <div style={{
                            fontSize: '12px',
                            lineHeight: '1.4',
                            fontWeight: '500'
                          }}>
                            {event.customer_name || '-'}
                          </div>
                        </td>
                        <td style={{padding: '12px 8px', textAlign: 'center'}}>
                          {event.thumbnail ? (
                            <img 
                              src={event.thumbnail} 
                              alt="Thumbnail"
                              style={{
                                width: '80px',
                                height: '60px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                border: '1px solid #e5e7eb'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedThumbnail(event.thumbnail);
                              }}
                            />
                          ) : (
                            <span style={{color: '#9ca3af', fontSize: '11px'}}>📷 Kein Bild</span>
                          )}
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default KasseModule;
